// Chat positioning configuration and utilities
export const CHAT_POSITIONING = {
  // Base dimensions
  BASE_OFFSET: 12,
  BOTTOM_OFFSET: 12, // Consistent bottom spacing for all chats
  CHAT_SPACING: 8, // Unified compact spacing
  CHAT_CONTAINER_PADDING: 24, // p-3 on both sides (12px each)
  
  // Chat list dimensions - responsive
  CHAT_LIST_EXPANDED_MOBILE: 320, // w-80 mobile
  CHAT_LIST_EXPANDED_DESKTOP: 384, // w-96 desktop
  CHAT_LIST_COLLAPSED: 160, // collapsed trigger approximate width
  
  // Chat window dimensions (content width only)
  REGULAR_CHAT_WIDTH: 350,
  EXPANDED_CHAT_WIDTH: 500,
  MINIMIZED_CHAT_FIXED_WIDTH: 208, // w-52 desktop
  
  // Z-index hierarchy
  Z_INDEX: {
    CHAT_LIST: 30,
    MINIMIZED_CHATS: 35,
    REGULAR_CHATS: 40,
    EXPANDED_CHATS: 45,
  } as const,
} as const;

export type ChatState = {
  isMinimized: boolean;
  isExpanded: boolean;
};

export function calculateChatPosition(
  chatIndex: number,
  currentChatState: ChatState,
  isChatListExpanded: boolean,
  previousChatStates: ChatState[] = []
): { right: string; bottom: string; zIndex: number } {
  const { 
    CHAT_LIST_EXPANDED_MOBILE, 
    CHAT_LIST_EXPANDED_DESKTOP, 
    CHAT_LIST_COLLAPSED
  } = CHAT_POSITIONING;
  
  // Get responsive adjustments
  const responsive = getResponsiveAdjustments();
  const BASE_OFFSET = responsive.BASE_OFFSET ?? CHAT_POSITIONING.BASE_OFFSET;
  const CHAT_SPACING = responsive.CHAT_SPACING ?? CHAT_POSITIONING.CHAT_SPACING;
  const BOTTOM_OFFSET = responsive.BOTTOM_OFFSET ?? CHAT_POSITIONING.BOTTOM_OFFSET;

  // Calculate chat list width
  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 768;
  const expandedWidthDefault = isDesktop ? CHAT_LIST_EXPANDED_DESKTOP : CHAT_LIST_EXPANDED_MOBILE;
  let chatListWidth: number = CHAT_LIST_COLLAPSED;
  
  if (typeof document !== 'undefined') {
    if (isChatListExpanded) {
      const panel = document.getElementById('chat-list-expanded');
      chatListWidth = (panel?.offsetWidth || 0) || expandedWidthDefault;
    } else {
      const trigger = document.getElementById('chat-list-collapsed');
      chatListWidth = (trigger?.offsetWidth || 0) || CHAT_LIST_COLLAPSED;
    }
  } else {
    chatListWidth = isChatListExpanded ? expandedWidthDefault : CHAT_LIST_COLLAPSED;
  }
  
  const chatListOffset = chatListWidth + CHAT_SPACING;
  
  // Calculate cumulative width from previous chats
  const cumulativeWidth = previousChatStates.reduce((total, state) => {
    const width = getChatWidthWithPadding(state);
    return total + width + CHAT_SPACING;
  }, 0);
  
  // Calculate final position
  const rightPosition = BASE_OFFSET + chatListOffset + cumulativeWidth;
  
  // Determine z-index
  let zIndex: number = CHAT_POSITIONING.Z_INDEX.REGULAR_CHATS;
  if (currentChatState.isMinimized) {
    zIndex = CHAT_POSITIONING.Z_INDEX.MINIMIZED_CHATS;
  } else if (currentChatState.isExpanded) {
    zIndex = CHAT_POSITIONING.Z_INDEX.EXPANDED_CHATS;
  }
  
  return {
    right: `${rightPosition}px`,
    bottom: `${BOTTOM_OFFSET}px`,
    zIndex
  };
}


function getChatWidth(state: ChatState): number {
  const { REGULAR_CHAT_WIDTH, EXPANDED_CHAT_WIDTH, MINIMIZED_CHAT_FIXED_WIDTH } = CHAT_POSITIONING;
  
  if (state.isMinimized) {
    const responsive = getResponsiveAdjustments();
    return (responsive as any).MINIMIZED_CHAT_FIXED_WIDTH ?? MINIMIZED_CHAT_FIXED_WIDTH;
  } else if (state.isExpanded) {
    return EXPANDED_CHAT_WIDTH;
  } else {
    return REGULAR_CHAT_WIDTH;
  }
}

// Always returns full container footprint (content width + ChatContainer's p-3 padding)
function getChatWidthWithPadding(state: ChatState): number {
  const baseWidth = getChatWidth(state);
  
  // Minimized chats handle their own padding (px-3 in ChatMinimized.tsx)
  // Regular and expanded chats need container padding (ChatContainer's p-3)
  if (state.isMinimized) {
    return baseWidth; // No external padding for minimized chats
  }
  
  return baseWidth + CHAT_POSITIONING.CHAT_CONTAINER_PADDING;
}

// Responsive positioning adjustments
export function getResponsiveAdjustments() {
  if (typeof window === 'undefined') return {};
  
  const isMobile = window.innerWidth < 768;
  const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
  
  if (isMobile) {
    return {
      BASE_OFFSET: 8 as const,
      BOTTOM_OFFSET: 8 as const,
      CHAT_SPACING: 6 as const,
      MINIMIZED_CHAT_FIXED_WIDTH: 176 as const, // w-44
      CHAT_LIST_COLLAPSED: 120 as const,
    };
  } else if (isTablet) {
    return {
      BASE_OFFSET: 10 as const,
      BOTTOM_OFFSET: 10 as const,
      CHAT_SPACING: 8 as const,
      MINIMIZED_CHAT_FIXED_WIDTH: 192 as const, // w-48
    };
  }
  
  return {
    BASE_OFFSET: 12 as const,
    BOTTOM_OFFSET: 12 as const,
    CHAT_SPACING: 8 as const,
    MINIMIZED_CHAT_FIXED_WIDTH: 208 as const, // w-52
  };
}
